import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { tilt } from "@/lib/tilt";
import { crossing } from "@/lib/crossing";
import { hush } from "@/lib/hush";
import { localHour } from "@/lib/hour";

const VERT = `
void main() {
  gl_Position = vec4(position, 1.0);
}
`;

const FRAG = `
precision highp float;
uniform vec2 u_res;
uniform vec2 u_light;
uniform vec2 u_point;
uniform vec3 u_tint;
uniform float u_time;
uniform float u_focus;
uniform vec3 u_base;
uniform float u_parallax;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)),
    u.x),
    u.y
  );
}

float dustLayer(vec2 uv, float t) {
  vec2 g = uv * 24.0;
  vec2 id = floor(g);
  vec2 f = fract(g) - 0.5;
  vec2 pos = (vec2(hash(id + 1.7), hash(id + 4.3)) - 0.5) * 0.7;
  float present = step(0.80, hash(id + 7.9));
  float d = length(f - pos);
  float dot = smoothstep(0.11, 0.0, d);
  float tw = 0.45 + 0.55 * sin(t * (0.5 + hash(id + 9.2)) + hash(id) * 40.0);
  return dot * present * tw;
}

void main() {
  float aspect = u_res.x / u_res.y;
  vec2 p = gl_FragCoord.xy / u_res;
  p.x *= aspect;
  vec2 lp = u_light / u_res;
  lp.x *= aspect;
  vec2 pp = u_point / u_res;
  pp.x *= aspect;

  float d = distance(p, lp);
  float atten = 1.0 / (1.0 + (d * d) / 0.012);

  float dp = distance(p, pp);
  float focal = exp(-(dp * dp) / 0.00018) * u_focus;

  float pr = u_parallax;
  float n1 = noise(p * 2.6 + vec2(u_time * 0.018, -u_time * 0.011 + pr));
  float n2 = noise(p * 6.1 + vec2(-u_time * 0.008, u_time * 0.013 + pr * 2.0));
  float filaments = smoothstep(0.42, 0.9, n1 * 0.6 + n2 * 0.4);

  // Far-off dust: a whisper-faint mote layer set deeper than the filaments,
  // drifting slowly and catching the light — depth behind the depth.
  vec2 dustUV = vec2(p.x + u_time * 0.003, p.y - u_time * 0.004 + pr * 0.4);
  float dust = dustLayer(dustUV, u_time);

  float lum = atten * (0.045 + filaments * 0.55) + focal * 0.22 + dust * (0.03 + atten * 0.32);
  gl_FragColor = vec4(u_base + u_tint * lum, 1.0);
}
`;

// The void's ambient character, per room of the site.
const AMBIENTS = {
  "/": { base: [0.039, 0.039, 0.082], temp: null }, // #0a0a15 — cool, oceanic threshold
  "/makers": { base: [0.039, 0.039, 0.082], temp: 3600 },
  "/knowledge": { base: [0.066, 0.047, 0.031], temp: 2200 }, // candlelit studio
  "/document": { base: [0.043, 0.032, 0.02], temp: 2200 },
  "/submit": { base: [0.102, 0.078, 0.063], temp: 1900 }, // #0B0B10 — amber, receiving
  "/threshold": { base: [0.102, 0.078, 0.063], temp: 1900 }, // the hidden room, candlelit
};

// Past the homepage Threshold, the landing's cool light warms to candle-gold.
const CANDLE_TEMP = 2100;
const CANDLE_BASE = [0.066, 0.047, 0.031];

// Homepage rooms (`data-room` on each section): the light matches each room's tint.
const ROOMS = {
  hero: { temp: 3800, base: [0.039, 0.039, 0.082] },
  cool: { temp: 7400, base: [0.038, 0.044, 0.104] }, // The Passage — blue-violet
  warm: { temp: 2900, base: [0.072, 0.052, 0.03] }, // Invitation — gold
  glass: { temp: 5600, base: [0.044, 0.05, 0.06] }, // Living Light — silver
};
const ROOM_REACH = 0.6; // of viewport height — how far a room's light spills through its doorway

// Local Hour Light: the void keeps the visitor's own hour — dawn and dusk gold,
// clear at midday, blue-white toward midnight. Keyframes are [hour, kelvin].
const HOUR_KEYS = [
  [0, 9400],
  [3, 10200],
  [5, 4200],
  [6.5, 2300],
  [9, 4300],
  [13, 5600],
  [17, 4100],
  [19, 2400],
  [21.5, 5800],
  [24, 9400],
];
const HOUR_WEIGHT = 0.42; // how much the hour colours each room's own light
const HOUR_BASE_WEIGHT = 0.3;
const HOUR_BASE_WARM = [0.07, 0.051, 0.03];
const HOUR_BASE_COOL = [0.034, 0.039, 0.098];
const HOUR_MS = 60_000;

function hourTemp(hour) {
  const h = ((hour % 24) + 24) % 24;
  for (let i = 1; i < HOUR_KEYS.length; i += 1) {
    const [h0, t0] = HOUR_KEYS[i - 1];
    const [h1, t1] = HOUR_KEYS[i];
    if (h <= h1) {
      const k = (h - h0) / (h1 - h0);
      return t0 + (t1 - t0) * (k * k * (3 - 2 * k));
    }
  }
  return HOUR_KEYS[0][1];
}

const INTERACTIVE = 'a, button, [role="button"], input, select, textarea, label, summary, [data-cursor]';
const HEADINGS = 'h1, h2, h3, h4, a[class*="D4AF37"], [data-lens]';
const GOLD = [132, 223, 244];
const WARM_RADIUS = 150; // px — the cursor warms toward gold within this reach of anything touchable
const RECTS_MS = 1000;
const TAU_WARM = 0.14;
const TAU_GLOW = 0.08; // seconds — glow trail decay
const TAU_LIGHT = 0.11; // shader light trail
const TAU_AMBIENT = 0.8; // room temperature drift
const TAU_TOUCH_IN = 0.12; // finger takes the light
const TAU_TOUCH_OUT = 0.7; // light drifts back after release (~2s)
const TAU_BLOOM = 0.75; // release bloom fade (~2s)
const RESIZE_MS = 120;

function kelvinToRGBJS(kelvin) {
  const t = Math.min(Math.max(kelvin, 1000), 12000) / 100;
  let r;
  let g;
  let b;
  if (t <= 66) {
    r = 255;
    g = 99.4708025861 * Math.log(t) - 161.1195681661;
  } else {
    r = 329.698727446 * Math.pow(t - 60, -0.1332047592);
    g = 288.1221695283 * Math.pow(t - 60, -0.0755148492);
  }
  if (t >= 66) {
    b = 255;
  } else if (t <= 19) {
    b = 0;
  } else {
    b = 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  }
  return [
    Math.round(Math.min(Math.max(r, 0), 255)),
    Math.round(Math.min(Math.max(g, 0), 255)),
    Math.round(Math.min(Math.max(b, 0), 255)),
  ];
}

const decay = (dt, tau) => 1 - Math.exp(-dt / tau);

// Resolves once the first frame has painted and the main thread is idle.
function afterFirstPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if ("requestIdleCallback" in window) window.requestIdleCallback(resolve, { timeout: 900 });
        else setTimeout(resolve, 260);
      })
    );
  });
}

export default function LightField() {
  const canvasRef = useRef(null);
  const glowRef = useRef(null);
  const coreRef = useRef(null);
  const { pathname } = useLocation();
  const ambientRef = useRef(AMBIENTS["/"]);
  const refreshRef = useRef(null);

  useEffect(() => {
    const key =
      Object.keys(AMBIENTS).find((k) => k !== "/" && pathname.startsWith(k)) ||
      "/";
    ambientRef.current = AMBIENTS[key];
    const t = setTimeout(() => refreshRef.current?.(), 900);
    return () => clearTimeout(t);
  }, [pathname]);

  useEffect(() => {
    let disposed = false;
    let teardown = () => {};

    const boot = async () => {
      await afterFirstPaint();
      const THREE = await import("three");
      if (disposed) return;
      teardown = start(THREE);
    };

    const start = (THREE) => {
      const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const finePointer = window.matchMedia("(pointer: fine)").matches;

      const canvas = canvasRef.current;
      const renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: false,
        alpha: false,
        powerPreference: finePointer ? "high-performance" : "low-power",
      });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, finePointer ? 1 : 1.25));
      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      const uniforms = {
        u_res: { value: new THREE.Vector2(1, 1) },
        u_light: { value: new THREE.Vector2(-10000, -10000) },
        u_point: { value: new THREE.Vector2(-10000, -10000) },
        u_tint: { value: new THREE.Vector3(1, 0.84, 0.66) },
        u_time: { value: 0 },
        u_focus: { value: 0 },
        u_parallax: { value: 0 },
        u_base: { value: new THREE.Vector3(...ambientRef.current.base) },
      };
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(2, 2),
        new THREE.ShaderMaterial({
          uniforms,
          vertexShader: VERT,
          fragmentShader: FRAG,
          depthTest: false,
          depthWrite: false,
        })
      );
      scene.add(mesh);

      const resize = () => {
        renderer.setSize(window.innerWidth, window.innerHeight, false);
        uniforms.u_res.value.set(canvas.width, canvas.height);
      };
      let resizeTimer = 0;
      const onResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          resize();
          refreshRef.current?.();
        }, RESIZE_MS);
      };
      resize();
      window.addEventListener("resize", onResize);

      const glow = glowRef.current;
      const core = coreRef.current;

      // Pointer state: exact position (core), trailing positions (glow, shader light).
      const ptr = { x: window.innerWidth / 2, y: window.innerHeight / 2, inside: false, hover: false };
      const glowPos = { x: ptr.x, y: ptr.y };
      const lightPos = { x: ptr.x, y: ptr.y };
      const last = { x: ptr.x, y: ptr.y };
      let stretch = 0;
      let angle = 0;
      let focus = 0;
      let focusTarget = 0;

      // Touch: the finger becomes the light source; release leaves a bloom.
      const finger = { x: ptr.x, y: ptr.y, active: false };
      let touchK = 0;
      let bloom = 0;

      const placeCore = () => {
        core.style.transform = `translate3d(${ptr.x}px, ${ptr.y}px, 0) translate(-50%, -50%)`;
      };
      const setPresence = (on) => {
        ptr.inside = on;
        focusTarget = on ? 1 : 0;
        glow.style.opacity = on ? "1" : "0";
        core.style.opacity = on ? "1" : "0";
      };
      const onMove = (e) => {
        ptr.x = e.clientX;
        ptr.y = e.clientY;
        if (!ptr.inside) setPresence(true);
        placeCore();
      };
      const onLeave = (e) => {
        if (e && e.relatedTarget) return;
        if (ptr.inside) setPresence(false);
      };
      const isInteractive = (node) => !!(node && node.closest && node.closest(INTERACTIVE));
      const disperse = (e) => {
        const h = e.target && e.target.closest ? e.target.closest(HEADINGS) : null;
        if (!h || h.classList.contains("lens-disperse")) return;
        if (e.relatedTarget && h.contains(e.relatedTarget)) return;
        const r = h.getBoundingClientRect();
        h.style.setProperty("--lens-dir", e.clientX < r.left + r.width / 2 ? "1" : "-1");
        h.classList.add("lens-disperse");
        h.addEventListener("animationend", () => h.classList.remove("lens-disperse"), { once: true });
      };
      const onOver = (e) => {
        if (!ptr.hover && isInteractive(e.target)) {
          ptr.hover = true;
          core.classList.add("is-ring");
        }
        disperse(e);
      };
      const onOut = (e) => {
        if (ptr.hover && !isInteractive(e.relatedTarget)) {
          ptr.hover = false;
          core.classList.remove("is-ring");
        }
      };
      const onTouchDown = (e) => {
        if (e.pointerType !== "touch") return;
        finger.x = e.clientX;
        finger.y = e.clientY;
        finger.active = true;
      };
      const onTouchMove = (e) => {
        if (e.pointerType !== "touch" || !finger.active) return;
        finger.x = e.clientX;
        finger.y = e.clientY;
      };
      const onTouchUp = (e) => {
        if (e.pointerType !== "touch" || !finger.active) return;
        finger.active = false;
        bloom = 1;
      };
      if (!still) {
        if (finePointer) {
          window.addEventListener("pointermove", onMove, { passive: true });
          document.addEventListener("mouseout", onLeave, { passive: true });
          window.addEventListener("blur", onLeave);
          document.addEventListener("pointerover", onOver, { passive: true });
          document.addEventListener("pointerout", onOut, { passive: true });
        } else {
          window.addEventListener("pointerdown", onTouchDown, { passive: true });
          window.addEventListener("pointermove", onTouchMove, { passive: true });
          window.addEventListener("pointerup", onTouchUp, { passive: true });
          window.addEventListener("pointercancel", onTouchUp, { passive: true });
        }
      }

      // Rooms: the landing's light follows whichever room holds the viewport's
      // heart, blending across doorways. Anything unroomed rests at the hero's light.
      let rooms = [];
      const refreshRooms = () => {
        const sy = window.scrollY;
        rooms = Array.from(document.querySelectorAll("[data-room]")).flatMap((el) => {
          const def = ROOMS[el.dataset.room];
          if (!def) return [];
          const r = el.getBoundingClientRect();
          return [{ top: r.top + sy, bottom: r.bottom + sy, temp: def.temp, base: def.base }];
        });
      };
      const roomTarget = () => {
        const c = window.scrollY + window.innerHeight / 2;
        const reach = window.innerHeight * ROOM_REACH;
        let wsum = 0;
        let t = 0;
        let b0 = 0;
        let b1 = 0;
        let b2 = 0;
        for (let i = 0; i < rooms.length; i += 1) {
          const r = rooms[i];
          const d = Math.max(r.top - c, 0, c - r.bottom);
          if (d >= reach) continue;
          const k = 1 - d / reach;
          const w = k * k * (3 - 2 * k);
          wsum += w;
          t += w * r.temp;
          b0 += w * r.base[0];
          b1 += w * r.base[1];
          b2 += w * r.base[2];
        }
        if (wsum < 1) {
          const rest = 1 - wsum;
          t += rest * ROOMS.hero.temp;
          b0 += rest * ROOMS.hero.base[0];
          b1 += rest * ROOMS.hero.base[1];
          b2 += rest * ROOMS.hero.base[2];
          wsum = 1;
        }
        return { temp: t / wsum, base: [b0 / wsum, b1 / wsum, b2 / wsum] };
      };
      refreshRooms();

      // Touchable geometry, cached in page space (viewport space for fixed chrome).
      let rects = [];
      const refreshRects = () => {
        const sx = window.scrollX;
        const sy = window.scrollY;
        const out = [];
        document.querySelectorAll(INTERACTIVE).forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.width < 2 || r.height < 2) return;
          const fixed = !!el.closest(".fixed");
          out.push(
            fixed
              ? [r.left, r.top, r.right, r.bottom, true]
              : [r.left + sx, r.top + sy, r.right + sx, r.bottom + sy, false]
          );
        });
        rects = out;
      };
      const nearestWarmth = () => {
        if (!rects.length) return 0;
        const sx = window.scrollX;
        const sy = window.scrollY;
        let best = WARM_RADIUS;
        for (let i = 0; i < rects.length; i += 1) {
          const [l, t, r, b, fixed] = rects[i];
          const px = fixed ? ptr.x : ptr.x + sx;
          const py = fixed ? ptr.y : ptr.y + sy;
          const dx = Math.max(l - px, 0, px - r);
          const dy = Math.max(t - py, 0, py - b);
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < best) best = d;
        }
        const k = 1 - best / WARM_RADIUS;
        return k * k * (3 - 2 * k);
      };
      let rectsTimer = 0;
      const refreshAll = () => {
        refreshRooms();
        if (finePointer && !still) refreshRects();
      };
      refreshRef.current = refreshAll;
      if (finePointer && !still) refreshRects();
      if (!still) {
        rectsTimer = setInterval(() => {
          if (!document.hidden) refreshAll();
        }, RECTS_MS);
      }
      let warmth = 0;
      let lastWarmth = -1;

      let temp = 4000;
      let lastGlowTemp = -1;
      let hourT = hourTemp(localHour());
      let hourAt = performance.now();
      let lastKelvinOut = -1;
      canvas.dataset.localHour = localHour().toFixed(2);
      let raf = 0;
      let running = true;
      const startAt = performance.now();
      let lastNow = startAt;
      let fieldTime = 0;
      let parallax = window.scrollY / Math.max(1, window.innerHeight);

      const frame = (now) => {
        if (!running) return;
        const dt = Math.min(Math.max((now - lastNow) / 1000, 0.001), 0.05);
        lastNow = now;
        // The Held Breath: when the void holds its breath, the field's motion
        // slows almost to stillness, then resumes.
        fieldTime += dt * (1 - 0.95 * hush.k);
        const t = fieldTime;
        uniforms.u_time.value = t;
        // Parallax Rooms: the light field drifts as the visitor scrolls, and its
        // two filament layers separate at different rates, lending the void depth.
        const scrollUnit = window.scrollY / Math.max(1, window.innerHeight);
        parallax += (scrollUnit - parallax) * decay(dt, 0.18);
        uniforms.u_parallax.value = parallax * 0.1;

        const W = window.innerWidth;
        const H = window.innerHeight;
        let tx = ptr.x;
        let ty = ptr.y;
        if (!finePointer || !ptr.inside) {
          tx = W * (0.5 + 0.3 * Math.sin(t * 0.11)) + tilt.x * W * 0.22;
          ty = H * (0.5 + 0.26 * Math.sin(t * 0.073 + 1.7)) + tilt.y * H * 0.18;
        }
        if (!finePointer) {
          touchK += ((finger.active ? 1 : 0) - touchK) * decay(dt, finger.active ? TAU_TOUCH_IN : TAU_TOUCH_OUT);
          tx += (finger.x - tx) * touchK;
          ty += (finger.y - ty) * touchK;
          bloom += (0 - bloom) * decay(dt, TAU_BLOOM);
        }
        const kGlow = decay(dt, TAU_GLOW);
        glowPos.x += (tx - glowPos.x) * kGlow;
        glowPos.y += (ty - glowPos.y) * kGlow;
        const kLight = decay(dt, TAU_LIGHT);
        lightPos.x += (tx - lightPos.x) * kLight;
        lightPos.y += (ty - lightPos.y) * kLight;

        // Velocity → glow stretch along the direction of travel.
        const vx = (tx - last.x) / dt;
        const vy = (ty - last.y) / dt;
        last.x = tx;
        last.y = ty;
        const speed = Math.hypot(vx, vy);
        const stretchTarget = Math.min(speed / 2600, 0.38);
        stretch += (stretchTarget - stretch) * decay(dt, 0.09);
        if (speed > 40) angle = Math.atan2(vy, vx);

        const ambient = ambientRef.current;
        const kA = decay(dt, TAU_AMBIENT);
        const landing = ambient.temp === null;
        const kc = landing ? crossing.k : 0;
        const room = landing ? roomTarget() : null;
        const baseT = landing ? room.base : ambient.base;
        if (now - hourAt > HOUR_MS) {
          hourAt = now;
          hourT = hourTemp(localHour());
          canvas.dataset.localHour = localHour().toFixed(2);
        }
        const hourN = Math.min(Math.max((hourT - 2300) / 7900, 0), 1);
        const hb0 = HOUR_BASE_WARM[0] + (HOUR_BASE_COOL[0] - HOUR_BASE_WARM[0]) * hourN;
        const hb1 = HOUR_BASE_WARM[1] + (HOUR_BASE_COOL[1] - HOUR_BASE_WARM[1]) * hourN;
        const hb2 = HOUR_BASE_WARM[2] + (HOUR_BASE_COOL[2] - HOUR_BASE_WARM[2]) * hourN;
        const roomTemp = landing ? room.temp + (CANDLE_TEMP - room.temp) * kc : ambient.temp;
        const tempTarget = roomTemp + (hourT - roomTemp) * HOUR_WEIGHT * (1 - kc);
        temp += (tempTarget - temp) * kA;
        if (Math.abs(temp - lastKelvinOut) > 25) {
          lastKelvinOut = temp;
          canvas.dataset.kelvin = Math.round(temp);
        }
        const [tr, tg, tb] = kelvinToRGBJS(temp);
        const hushDim = 1 - 0.5 * hush.k;
        uniforms.u_tint.value.set((tr / 255) * hushDim, (tg / 255) * hushDim, (tb / 255) * hushDim);
        const base = uniforms.u_base.value;
        const hw = HOUR_BASE_WEIGHT * (1 - kc);
        const bt0 = baseT[0] + (CANDLE_BASE[0] - baseT[0]) * kc;
        const bt1 = baseT[1] + (CANDLE_BASE[1] - baseT[1]) * kc;
        const bt2 = baseT[2] + (CANDLE_BASE[2] - baseT[2]) * kc;
        base.x += (bt0 + (hb0 - bt0) * hw - base.x) * kA;
        base.y += (bt1 + (hb1 - bt1) * hw - base.y) * kA;
        base.z += (bt2 + (hb2 - bt2) * hw - base.z) * kA;

        const focusT = finePointer ? focusTarget : Math.max(touchK * 0.7, bloom);
        focus += (focusT - focus) * kGlow;
        uniforms.u_focus.value = focus;
        const dpr = renderer.getPixelRatio();
        uniforms.u_light.value.set(lightPos.x * dpr, (H - lightPos.y) * dpr);
        const px = finePointer ? ptr.x : finger.x;
        const py = finePointer ? ptr.y : finger.y;
        uniforms.u_point.value.set(px * dpr, (H - py) * dpr);
        renderer.render(scene, camera);

        if (finePointer) {
          const warmT = ptr.hover ? 1 : ptr.inside ? nearestWarmth() : 0;
          warmth += (warmT - warmth) * decay(dt, TAU_WARM);
          glow.style.transform = `translate3d(${glowPos.x}px, ${glowPos.y}px, 0) translate(-50%, -50%) rotate(${angle}rad) scale(${(1 + stretch) * (1 + warmth * 0.16)}, ${(1 - stretch * 0.4) * (1 + warmth * 0.16)})`;
          if (Math.abs(temp - lastGlowTemp) > 24 || Math.abs(warmth - lastWarmth) > 0.015) {
            lastGlowTemp = temp;
            lastWarmth = warmth;
            const rgb = `${Math.round(tr + (GOLD[0] - tr) * warmth)},${Math.round(tg + (GOLD[1] - tg) * warmth)},${Math.round(tb + (GOLD[2] - tb) * warmth)}`;
            glow.style.setProperty("--cursor-rgb", rgb);
            core.style.setProperty("--cursor-rgb", rgb);
            glow.style.setProperty("--cursor-warm", warmth.toFixed(3));
          }
        }
        if (!still) raf = requestAnimationFrame(frame);
      };

      // Reduced motion: one still frame of the field, lit from its resting place.
      if (still) {
        lightPos.x = window.innerWidth * 0.5;
        lightPos.y = window.innerHeight * 0.42;
        frame(performance.now() + 1);
      } else {
        raf = requestAnimationFrame(frame);
      }
      canvas.classList.add("is-lit");

      const onVisibility = () => {
        if (still) return;
        if (document.hidden) {
          running = false;
          cancelAnimationFrame(raf);
        } else if (!running) {
          running = true;
          lastNow = performance.now();
          raf = requestAnimationFrame(frame);
        }
      };
      document.addEventListener("visibilitychange", onVisibility);

      return () => {
        running = false;
        cancelAnimationFrame(raf);
        clearTimeout(resizeTimer);
        clearInterval(rectsTimer);
        refreshRef.current = null;
        window.removeEventListener("resize", onResize);
        window.removeEventListener("pointermove", onMove);
        document.removeEventListener("mouseout", onLeave);
        window.removeEventListener("blur", onLeave);
        document.removeEventListener("pointerover", onOver);
        document.removeEventListener("pointerout", onOut);
        window.removeEventListener("pointerdown", onTouchDown);
        window.removeEventListener("pointermove", onTouchMove);
        window.removeEventListener("pointerup", onTouchUp);
        window.removeEventListener("pointercancel", onTouchUp);
        document.removeEventListener("visibilitychange", onVisibility);
        renderer.dispose();
      };
    };

    boot();
    return () => {
      disposed = true;
      teardown();
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        data-testid="light-field-canvas"
        aria-hidden="true"
        className="light-field pointer-events-none fixed inset-0 z-0 h-full w-full"
      />
      <div
        ref={glowRef}
        data-testid="cursor-light"
        aria-hidden="true"
        className="cursor-glow pointer-events-none fixed left-0 top-0 z-[9998] hidden h-44 w-44 rounded-full will-change-transform [@media(pointer:fine)]:block"
      />
      <div
        ref={coreRef}
        data-testid="cursor-core"
        aria-hidden="true"
        className="cursor-core pointer-events-none fixed left-0 top-0 z-[9999] hidden rounded-full will-change-transform [@media(pointer:fine)]:block"
      />
    </>
  );
}
